import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	Router,
	RouterStateSnapshot,
	UrlTree,
} from '@angular/router';
import { firstValueFrom, of, timeout } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MaintenanceRepository } from '~shared/store/maintenance/maintenance.repository';
import { AuthService } from '~core/services/auth.service';
import { UserRole, UserType } from '~shared/enums/user.enum';

const MAINTENANCE_PATH = '/maintenance';
const USER_LOAD_TIMEOUT_MS = 5_000;

/**
 * Routes everyone to the maintenance page while maintenance is on, and bounces
 * them back off it once it's lifted. Two exceptions:
 *  - internal ADMINs bypass maintenance entirely, so they can still reach the
 *    controls (and the rest of the app) to switch it back off;
 *  - a `?preview` link to the maintenance page is always allowed.
 *
 * When maintenance is on the decision waits for the signed-in user (incl. role)
 * to fully resolve, so an admin is never briefly flashed the maintenance page.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceGuard {
	constructor(
		private readonly router: Router,
		private readonly maintenanceRepository: MaintenanceRepository,
		private readonly authService: AuthService,
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
		return this._check(state.url);
	}

	canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
		return this._check(state.url);
	}

	private async _check(url: string): Promise<boolean | UrlTree> {
		const [path, query = ''] = url.split('?');
		const onMaintenancePage = path === MAINTENANCE_PATH;
		const isPreview = new URLSearchParams(query).has('preview');

		// Preview tab: show the page as-is, whatever the live flag says.
		if (onMaintenancePage && isPreview) {
			return true;
		}

		if (!this.maintenanceRepository.isInMaintenance()) {
			// Not in maintenance — don't strand anyone on the public page.
			return onMaintenancePage ? this.router.parseUrl('/') : true;
		}

		// Maintenance is on: fully resolve the caller (incl. role) before deciding,
		// so an admin is never flashed the maintenance page mid-load.
		if (await this.isInternalAdmin()) {
			return onMaintenancePage ? this.router.parseUrl('/') : true;
		}

		return onMaintenancePage ? true : this.router.parseUrl(MAINTENANCE_PATH);
	}

	private async isInternalAdmin(): Promise<boolean> {
		// 1. Wait for Cognito to settle (token loaded, or confirmed logged-out).
		if (this.authService.cognitoUserState.getValue() === 'loading') {
			await firstValueFrom(
				this.authService.cognitoUserState$.pipe(filter((state) => state === 'ready')),
			);
		}

		// 2. No token → logged out → not an admin (decide immediately, no waiting).
		if (!this.authService.jwtToken) {
			return false;
		}

		// 3. Session exists but the app user (with role) may still be loading —
		//    'ready' fires before currentUser$ is populated. Wait for it, bounded.
		let user = this.authService.currentUser$.value;
		if (!user) {
			user = await firstValueFrom(
				this.authService.currentUser$.pipe(
					filter((current) => !!current),
					timeout({ first: USER_LOAD_TIMEOUT_MS, with: () => of(null) }),
				),
			);
		}

		return user?.userType === UserType.INTERNAL && user?.userRole === UserRole.ADMIN;
	}
}
